import { useEffect, useState } from 'react';
import { getUserData } from '../utils';
import { useAddresses } from '../services/inner_layout/addresses';
import { usePaymentMethods } from '../services/inner_layout/paymentMethods';
import { useListPasskeys } from '../services/inner_layout/passkey';
import { IProfile } from '../types';

export const useCurrentNotification = () => {
  const [currentNotification, setCurrentNotification] = useState<
    'profile' | 'payment' | 'passkey' | 'address' | undefined
  >();
  const profileData = getUserData();

  const [profile, setProfile] = useState<IProfile>(profileData);
  const noProfileInfo = !profile?.fullName;

  const fetchPasskey = !noProfileInfo;
  const { data: passkeyData, isLoading: isLoadingPasskey } =
    useListPasskeys(fetchPasskey);
  const hasPasskey = isLoadingPasskey || !!passkeyData?.length;

  const fetchPaymentMethods = !noProfileInfo && hasPasskey && !isLoadingPasskey;
  const { data: paymentMethodsResponse, isLoading: isLoadingPaymentMethods } =
    usePaymentMethods(fetchPaymentMethods);
  const noPayment = !paymentMethodsResponse?.count;

  const fetchAddresses =
    fetchPaymentMethods && !noPayment && !isLoadingPaymentMethods;
  const { data: shippingAddressesResponse, isLoading: isLoadingShipping } =
    useAddresses('SHIPPING', fetchAddresses);
  const shippingAddressesDataLength =
    isLoadingShipping || shippingAddressesResponse?.count;

  const fetchBilling =
    fetchAddresses && !!shippingAddressesDataLength && !isLoadingShipping;
  const { data: billingAddressesResponse, isLoading: isLoadingBilling } =
    useAddresses('BILLING', fetchBilling);
  const billingAddressesDataLength =
    isLoadingBilling || billingAddressesResponse?.count;

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent | CustomEvent) => {
      if ('detail' in event) {
        // Custom event (same-tab changes)
        const { key } = event.detail;
        if (key === 'profile') {
          setProfile(profileData);
        }
      } else {
        // Native storage event (cross-tab changes)
        const { key } = event;
        if (key === 'profile') {
          setProfile(profileData);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange as EventListener);
    window.addEventListener(
      'localStorageChange',
      handleStorageChange as EventListener
    );

    return () => {
      window.removeEventListener(
        'storage',
        handleStorageChange as EventListener
      );
      window.removeEventListener(
        'localStorageChange',
        handleStorageChange as EventListener
      );
    };
  }, []);

  useEffect(() => {
    if (
      isLoadingPasskey ||
      isLoadingPaymentMethods ||
      isLoadingBilling ||
      isLoadingShipping
    )
      return;
    if (!profile?.fullName) return setCurrentNotification('profile');
    if (!isLoadingPaymentMethods && !isLoadingBilling && !isLoadingShipping) {
      if (noPayment) return setCurrentNotification('payment');
      else if (!hasPasskey) return setCurrentNotification('passkey');
      else if (!billingAddressesDataLength && !shippingAddressesDataLength)
        return setCurrentNotification('address');
      return setCurrentNotification(undefined);
    }
  }, [
    profile?.fullName,
    hasPasskey,
    noPayment,
    billingAddressesDataLength,
    shippingAddressesDataLength,
  ]);
  return { currentNotification, setCurrentNotification };
};
